import React, {Suspense} from 'react';
import BatteryDischargeRisk, {BatteryDischargeRiskLoading} from '@di-shared/features/diag/battery/discharge/BatteryDischargeRisk';
import {ErrorBoundary} from 'react-error-boundary';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import clsx from 'clsx';
import {ROUTE_PATHS} from '@di-shared/routes';
import {useNavigate} from 'react-router';
import {emitEventLog} from '@di-shared/features/analytics';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';

interface DiagBatteryDischargePageProps {
  vin: string;
}

function DiagBatteryDischargePage({vin}: DiagBatteryDischargePageProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {openAlert} = useCcWebviewUtilsHelper();
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T30077')}</h1>
      </Header>
      <main className="content page_car_management">
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<ConsentBannerLoadingKr />}>
            <ConsentBannerKr vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <div
          className="tabbar_txt tabbar_sticky"
          role="tablist"
        >
          <div className="inner">
            <button
              type="button"
              className={clsx('tabbar_item', 'body_large_r', {
                'is-selected': location.pathname === ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.getFullPath(vin),
              })}
              id={`${t('T30075')}_tab`}
              role="tab"
              aria-selected={location.pathname === ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.getFullPath(vin)}
              aria-controls={`${t('T30075')}_tabpanel`}
              tabIndex={location.pathname === ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.getFullPath(vin) ? 0 : -1}
            >
              <span className="txt">{t('T30075')}</span>
            </button>

            <button
              type="button"
              className={clsx('tabbar_item', 'body_large_r', {
                'is-selected': location.pathname === ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.getFullPath(vin),
              })}
              onClick={() => {
                emitEventLog({
                  event: 'vehicle_status_check',
                  interaction_type: 'view 12v battery soh',
                  click_text: t('T30076'),
                  click_location: 'body',
                });
                navigate(ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.getFullPath(vin), {replace: true});
              }}
              id={`${t('T30076')}_tab`}
              role="tab"
              aria-selected={location.pathname === ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.getFullPath(vin)}
              aria-controls={`${t('T30076')}_tabpanel`}
              tabIndex={location.pathname === ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.getFullPath(vin) ? 0 : -1}
            >
              <span className="txt">{t('T30076')}</span>
            </button>
          </div>
        </div>
        <ErrorBoundary
          fallbackRender={() => {
            (async function () {
              await openAlert({
                title: t('T20076'),
                message: t('T30358') + '\n' + t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
              });
              navigate(ROUTE_PATHS.HOME_VEHICLE_TAB.getFullPath(vin), {replace: true});
            })();
            return <></>;
          }}
        >
          <Suspense fallback={<BatteryDischargeRiskLoading />}>
            <BatteryDischargeRisk vin={vin} />
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

export default DiagBatteryDischargePage;
